import { config } from "../config";
import { ApiError } from "./errors";
import { callGateway, type CallerIdentity } from "./gateway";
import { cartAddons, type CartAddon, type CartLine, type CartSelection } from "./cart";
import { getRechargeOptions, whyNotRechargeable, type RechargeMethod, type RechargePlan, type SaleReceipt } from "./subscriberRecharge";
import {
  addressBody,
  addressErrors,
  installationBody,
  installationErrors,
  installationsDone,
  installationsMissing,
  DEFAULT_INSTALLATION,
  EMPTY_ADDRESS,
  type AddressForm,
  type AddressInput,
  type InstallationForm,
  type InstallationInput,
  type LoginRole,
} from "./installations";

/**
 * Staff "Create subscriber" (owner 2026-10-14): ONE call to radaccess
 * POST /access/operator/{ns}/subscribers. radaccess registers the subscriber
 * (radcustomer), then applies the plan through raduser's renew -- which writes
 * the password and spends the pin, if the plan uses one, in one transaction.
 * The password is generated by the service and never returned: the
 * subscriber gets it by email, once they confirm their address (owner
 * 2026-10-18/19; radius-wiki subscriber-cart-v1 §12.3). Staff never see it.
 *
 * RBAC resource `subscribercreate` (superadmin and Operator), the same pair
 * that may recharge.
 */
export type Gender = "male" | "female";

/** The server's email rule (radaccess profileEmail). */
export const EMAIL_FORM = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export interface CreateSubscriberInput {
  mobile: string;
  name: string;
  gender: Gender;
  plan: string;
  deviceid?: string;
  idprooftype?: string;
  idproofnumber?: string;
  /** Optional: the login details go here once the subscriber confirms it. */
  email?: string;
  /** Subscriber cart v1: left out when none are chosen. */
  addons?: CartAddon[];
  /** Required by the server when the cart total is above ₹0. */
  payment?: CreatePayment;
  /** Wired plan only (§10.1): required there, never sent for hotspot. */
  billing_address?: AddressInput;
  /** Wired plan only: one per login (mobile, and the ONU). No equipment: the ONU is recorded from its id. */
  installations?: InstallationInput[];
}

/** How the cart total was taken; the Recharge screen's methods and rules. */
export interface CreatePayment {
  method: RechargeMethod;
  reference: string;
  /** Cash only, where the namespace asks for it (api/cashCode.ts). */
  cash_code?: string;
}

export interface CreatedSubscriber {
  mobile: string;
  /** "" when no device was given. */
  deviceid: string;
  name: string;
  customerid: string;
  accountid: string;
  plan: string;
  /** RFC 3339, UTC. */
  expiry: string;
  /**
   * What happened to the login details (radaccess v1.125): "confirm_sent" --
   * a confirm link went to the email, the password follows once it is
   * opened; "not_sent" -- no email (or it could not be sent). Absent on a
   * retry that only completed the device or the installations.
   */
  login_details?: LoginDetailsStatus;
  /**
   * "device": a retry after a device half-failure (200). Only the device was
   * added; the mobile's password is unchanged.
   * "installations": a retry after a 424 on the installations (200). Only the
   * missing installations were added.
   */
  completed?: "device" | "installations";
  /** The logins whose installation (location and equipment) is saved; [] for hotspot. */
  installations_done?: string[];
  /** The cart total recorded (radaccess subscribercreate.go); absent on a pre-cart radaccess. */
  total?: number;
  /** The internet receipt made for the total; "" when the total was ₹0. */
  receiptno?: string;
  /** Every receipt of the sale: the internet one and, for non-internet items, a separate one. */
  receipts?: SaleReceipt[];
  /** The device limit the renew stated (0: the plan group's own). */
  simultaneoususe?: number;
  /** The cart's lines and receipt note; absent on a plan-only sale. */
  lines?: CartLine[];
  note?: string;
  /** The recurring add-ons were remembered for the next recharge. */
  addons_remembered?: boolean;
}

/** A completed-device retry: nothing new to give the subscriber but the device. */
export function isDeviceOnly(c: CreatedSubscriber): boolean {
  return c.completed === "device";
}

export type LoginDetailsStatus = "sent" | "confirm_sent" | "email_confirmed" | "not_sent";

/** The Create result's and the lookup card's line about the login details. */
export function loginDetailsLine(status: LoginDetailsStatus | undefined, email: string): string {
  switch (status) {
    case "sent":
      return `Login details emailed to ${email || "the subscriber"}`;
    case "confirm_sent":
      return `Confirm link sent to ${email || "the email"}; the login details follow once the subscriber opens it`;
    case "email_confirmed":
      return "Email confirmed; login details not sent yet";
    default:
      return email ? "Login details not sent yet" : "Login details not sent: no email -- add one in Edit profile, then Send login details";
  }
}

/** A retry that only completed the installations: the password is unchanged. */
export function isInstallationsOnly(c: CreatedSubscriber): boolean {
  return c.completed === "installations";
}

/** "Installations saved for X; the subscriber's password is unchanged". */
export function installationsCompletedMessage(c: CreatedSubscriber): string {
  const done = c.installations_done ?? [];
  return `Installations saved for ${done.length ? done.join(", ") : "every login"}; the subscriber's password is unchanged`;
}

/** What the form holds: every field as typed. */
export interface CreateSubscriberForm {
  mobile: string;
  name: string;
  /** "" until chosen: there is no default. */
  gender: "" | Gender;
  plan: string;
  deviceid: string;
  idprooftype: string;
  idproofnumber: string;
  /** Optional; "" sends none. */
  email: string;
  /** Add-on quantities chosen with the steppers. */
  addons: CartSelection;
  /** "" until chosen: asked only when the cart total is above ₹0. */
  paymethod: "" | RechargeMethod;
  payreference: string;
  /** Wired plan only. */
  billing: AddressForm;
  /** Wired plan only: per login, where it is installed; the device entry is used only when an ONU id is entered. */
  installs: Record<LoginRole, InstallationForm>;
}

export const EMPTY_CREATE_FORM: CreateSubscriberForm = {
  mobile: "",
  name: "",
  gender: "",
  plan: "",
  deviceid: "",
  idprooftype: "",
  idproofnumber: "",
  email: "",
  addons: {},
  paymethod: "",
  payreference: "",
  billing: EMPTY_ADDRESS,
  installs: { mobile: DEFAULT_INSTALLATION, device: DEFAULT_INSTALLATION },
};

/** The logins the form will create: the mobile, then the device when one is entered. */
export function createLogins(f: CreateSubscriberForm): { role: LoginRole; username: string }[] {
  const out: { role: LoginRole; username: string }[] = [{ role: "mobile", username: f.mobile.trim() }];
  const dev = f.deviceid.trim();
  if (dev) out.push({ role: "device", username: dev });
  return out;
}

/**
 * The distinct installation addresses this form names, counted as radaccess
 * counts them (installAddresses): the billing address when some login is
 * installed there, and each login's own address, without case or extra
 * spaces. A per-address one-time charge is capped at it (owner PC-1/PC-2).
 */
export function installAddresses(f: CreateSubscriberForm): number {
  const key = (a: AddressForm) => [a.address, a.city, a.state, a.pin].join(" | ").toLowerCase().split(/\s+/).filter(Boolean).join(" ");
  const seen = new Set<string>();
  const logins = createLogins(f);
  const own = logins.filter((l) => !f.installs[l.role].same_as_billing);
  for (const l of own) seen.add(key(f.installs[l.role].address));
  if (own.length < logins.length) seen.add(key(f.billing));
  return seen.size;
}

/**
 * What the form knows of the server's price: `total` is the quote's (null
 * when there is none -- loading, failed, or a pre-cart radaccess).
 */
export interface CreateCart {
  total: number | null;
  pending: boolean;
  /** The quote says the plan is wired: billing address and installations are asked. */
  wired?: boolean;
  /** How many more devices the add-ons let sign in at once (the quote's extradevices). No MAC is asked for them. */
  extraDevices?: number;
}

export const NO_CART: CreateCart = { total: null, pending: false, wired: false };

const path = (ns: string) => `/operator/${encodeURIComponent(ns)}/subscribers`;

/**
 * The JSON body: trimmed, with empty optional fields left out. `addons` only
 * when some are chosen; `payment` only when the quoted total is above ₹0;
 * `billing_address` and `installations` only for a wired plan.
 */
export function createBody(f: CreateSubscriberForm, total: number | null = null, wired = false): CreateSubscriberInput {
  const body: CreateSubscriberInput = { mobile: f.mobile.trim(), name: f.name.trim(), gender: f.gender as Gender, plan: f.plan };
  const deviceid = f.deviceid.trim();
  const idprooftype = f.idprooftype.trim();
  const idproofnumber = f.idproofnumber.trim();
  if (deviceid) body.deviceid = deviceid;
  if (idprooftype) body.idprooftype = idprooftype;
  if (idproofnumber) body.idproofnumber = idproofnumber;
  const email = f.email.trim().toLowerCase();
  if (email) body.email = email;
  const addons = cartAddons(f.addons);
  if (addons.length) body.addons = addons;
  if (total != null && total > 0 && f.paymethod) body.payment = { method: f.paymethod, reference: f.paymethod === "cash" ? "" : f.payreference.trim() };
  if (wired) {
    body.billing_address = addressBody(f.billing);
    // Owner 2026-10-19: only the ONU's MAC is asked -- it is the device
    // login; radaccess records the ONU from it. A phone changes its MAC, so
    // the mobile login and Extra logins carry none.
    body.installations = createLogins(f).map((l) => installationBody(l.username, { ...f.installs[l.role], equipment: [] }));
  }
  return body;
}

export async function createSubscriber(identity: CallerIdentity, ns: string, f: CreateSubscriberForm, total: number | null = null, wired = false): Promise<CreatedSubscriber> {
  return postCreateSubscriber(identity, ns, createBody(f, total, wired));
}

/** Sends a body as built; a retry after a 424 resends the same one. */
export async function postCreateSubscriber(identity: CallerIdentity, ns: string, body: CreateSubscriberInput): Promise<CreatedSubscriber> {
  const r = await callGateway<CreatedSubscriber>(identity, config().gatewayBase, path(ns), { method: "POST", body });
  return r.data;
}

/**
 * The namespace's plans, from the SAME source the Recharge dialog reads
 * (radaccess GET .../subscribers/{AccountID}/recharge, which radaccess also
 * uses to check the plan on create). A new subscriber has no account yet, so
 * the nil account id is asked for: it has no devices, and the plan list does
 * not depend on the account.
 */
export const NO_ACCOUNT = "00000000-0000-0000-0000-000000000000";

export async function listCreatePlans(identity: CallerIdentity, ns: string, signal?: AbortSignal): Promise<RechargePlan[]> {
  const r = await getRechargeOptions(identity, ns, NO_ACCOUNT, signal);
  return r.plans;
}

/** Why a plan cannot be given to a new subscriber, or "". Same rules as Recharge. */
export const whyNotCreatable = whyNotRechargeable;

/** The form's own checks, matching the server's; empty when it may be sent. */
export function createFormErrors(f: CreateSubscriberForm, cart: CreateCart = NO_CART): string[] {
  const out: string[] = [];
  if (!/^\d{10}$/.test(f.mobile.trim())) out.push("Mobile must be 10 digits");
  const name = f.name.trim();
  if (!name) out.push("Enter the subscriber's name");
  else if (name.length > 100) out.push("Name must be at most 100 characters");
  else if (!NAME_CHARS.test(name)) out.push(NAME_RULE);
  if (f.gender !== "male" && f.gender !== "female") out.push("Choose gender");
  if (!f.plan) out.push("Choose a plan");
  const dev = f.deviceid.trim();
  if (dev && !/^[A-Za-z0-9:._-]{1,64}$/.test(dev)) out.push("Device / ONU id: letters, digits and : . _ - only, at most 64");
  if (dev && dev === f.mobile.trim()) out.push("Device / ONU id must differ from the mobile");
  if (cart.wired && !dev) out.push("Enter the ONU id: a wired plan's ONU signs in with it");
  const t = f.idprooftype.trim();
  const n = f.idproofnumber.trim();
  if (t && !n) out.push("Enter the ID proof number, or clear the type");
  if (n && !t) out.push("Choose the ID proof type, or clear the number");
  if (f.email.trim() && !EMAIL_FORM.test(f.email.trim())) out.push("Email is not an email address");
  if (f.plan && cart.pending) out.push("Wait for the price");
  else if (f.plan && cart.total == null && cartAddons(f.addons).length) out.push("The add-ons could not be priced — clear them or try again");
  if (cart.total != null && cart.total > 0) {
    if (!f.paymethod) out.push("Choose the payment method");
    else if (f.paymethod === "upi" && !/^[A-Za-z0-9/_-]{6,40}$/.test(f.payreference.trim())) out.push("Enter the UPI reference (UTR), 6 to 40 letters or digits");
  }
  if (cart.wired) {
    out.push(...addressErrors(f.billing, "Billing address"));
    for (const l of createLogins(f)) out.push(...installationErrors({ ...f.installs[l.role], equipment: [] }, l.username || l.role));
  }
  return out;
}

/**
 * A person's name: letters (any script), spaces and ' . , - ( ). The rule
 * User diagnose checks (userdiagnose.php's); Create refuses what it would flag.
 */
export const NAME_CHARS = /^[\p{L}\p{M}'., \-()]+$/u;
export const NAME_RULE = "Name: letters and spaces only (also ' . , - ( )) — no digits or other symbols";

/** The characters of a name the rule refuses, each once, in order: "1", "@ 1". */
export function badNameChars(name: string): string {
  const seen: string[] = [];
  for (const ch of name) if (!NAME_CHARS.test(ch) && !seen.includes(ch)) seen.push(ch);
  return seen.join(" ");
}

/**
 * The same checks as createFormErrors, keyed by the field they belong to, so
 * each shows in red on its own field. `all` false (Create not yet pressed):
 * only what is typed and wrong; an empty required field waits for Create.
 * Keys: mobile, name, gender, plan, deviceid, idprooftype, idproofnumber, email,
 * paymethod, payreference, billing.<f>, install.<role>.<f>,
 * install.<role>.eq.<i>.mac_address.
 */
export function createFieldErrors(f: CreateSubscriberForm, cart: CreateCart = NO_CART, all = true): Record<string, string> {
  const out: Record<string, string> = {};
  const put = (k: string, msg: string, typed: boolean) => {
    if ((all || typed) && !(k in out)) out[k] = msg;
  };
  const mobile = f.mobile.trim();
  if (!/^\d{10}$/.test(mobile)) put("mobile", "10 digits", mobile !== "");
  const name = f.name.trim();
  if (!name) put("name", "Enter the name", false);
  else if (name.length > 100) put("name", "At most 100 characters", true);
  else if (!NAME_CHARS.test(name)) put("name", `Not allowed in a name: ${badNameChars(name)}`, true);
  if (f.gender !== "male" && f.gender !== "female") put("gender", "Choose", false);
  if (!f.plan) put("plan", "Choose a plan", false);
  const dev = f.deviceid.trim();
  if (dev && !/^[A-Za-z0-9:._-]{1,64}$/.test(dev)) put("deviceid", "Letters, digits and : . _ - only, at most 64", true);
  if (dev && dev === mobile) put("deviceid", "Must differ from the mobile", true);
  if (cart.wired && !dev) put("deviceid", "Enter the ONU id", false);
  const t = f.idprooftype.trim();
  const n = f.idproofnumber.trim();
  if (t && !n) put("idproofnumber", "Enter the number, or clear the type", true);
  if (n && !t) put("idprooftype", "Choose the type, or clear the number", true);
  if (f.email.trim() && !EMAIL_FORM.test(f.email.trim())) put("email", "Not an email address", true);
  if (cart.total != null && cart.total > 0) {
    if (!f.paymethod) put("paymethod", "Choose the payment method", false);
    else if (f.paymethod === "upi" && !/^[A-Za-z0-9/_-]{6,40}$/.test(f.payreference.trim()))
      put("payreference", "6 to 40 letters or digits", f.payreference.trim() !== "");
  }
  if (cart.wired) {
    addressFieldErrors(f.billing, "billing", put);
    for (const l of createLogins(f)) {
      const inst = f.installs[l.role];
      if (!inst.same_as_billing) addressFieldErrors(inst.address, `install.${l.role}`, put);
    }
  }
  return out;
}

function addressFieldErrors(a: AddressForm, prefix: string, put: (k: string, msg: string, typed: boolean) => void) {
  if (!a.address.trim()) put(`${prefix}.address`, "Enter the address", false);
  if (!a.city.trim()) put(`${prefix}.city`, "Enter the city", false);
  if (!a.state.trim()) put(`${prefix}.state`, "Enter the state", false);
  const pin = a.pin.trim();
  if (!/^[1-9]\d{5}$/.test(pin)) put(`${prefix}.pin`, "6 digits, not starting with 0", pin !== "");
}

export interface CreateFailure {
  title: string;
  message: string;
  /**
   * 424 (or 502): something landed; the message says what, and a retry is safe.
   * radaccess answers 424 where it answered 502, because Cloudflare in front of
   * access.ankhos.io turns an origin 502 into "Failed to fetch". A real gateway
   * 502 is still read the same way.
   */
  partial: boolean;
  /**
   * A 424 (or 502) naming a `deviceid` with no `installations_done`: the
   * mobile is live, this device failed. Retrying with the same details adds it.
   */
  deviceMissing?: string;
  /**
   * A 424 whose body has `installations_done`: the subscriber and plan are
   * live; these logins' installations were not saved. Retry resends the same body.
   */
  installationsMissing?: string[];
}

function bodyDevice(body: unknown): string {
  if (body && typeof body === "object" && "deviceid" in body) {
    const d = (body as { deviceid?: unknown }).deviceid;
    if (typeof d === "string") return d;
  }
  return "";
}

/**
 * How a failed create reads to the operator. The server's message is kept
 * wherever it says more. `sent` is the body that failed, to name the logins
 * whose installation did not land.
 */
export function createFailure(e: unknown, sent?: CreateSubscriberInput | null): CreateFailure {
  if (!(e instanceof ApiError)) return { title: "Not created", message: e instanceof Error ? e.message : String(e), partial: false };
  switch (e.status) {
    case 409:
      return { title: "Not created", message: e.message, partial: false };
    case 404:
      return { title: "Plan not found", message: `That plan is not in this namespace's catalog (${e.message}).`, partial: false };
    case 403:
      return { title: "Refused", message: "Your login cannot create subscribers here: it needs the Operator or superadmin role in this namespace.", partial: false };
    case 401:
      return { title: "Your session is not valid", message: "Sign in again.", partial: false };
    case 400:
      return { title: "Not created", message: e.message, partial: false };
    case 424:
    case 502: {
      const device = bodyDevice(e.body);
      const done = e.status === 424 ? installationsDone(e.body) : null;
      if (done) {
        const missing = installationsMissing(sent?.installations, done);
        const who = missing.length ? missing.join(", ") : "some logins";
        return {
          title: `Subscriber created; installation for ${who} not saved — Retry`,
          message: e.message,
          partial: true,
          installationsMissing: missing,
        };
      }
      if (device) return { title: "The mobile is live; the device was not added", message: e.message, partial: true, deviceMissing: device };
      return { title: "Partly done — read this before retrying", message: e.message, partial: true };
    }
  }
  if (e.kind === "fail-closed") return { title: "Not sent", message: e.message, partial: false };
  // A transport failure or a 5xx: the request may have landed. Retrying with
  // the same mobile is safe -- a finished create answers 409 "already a subscriber".
  return { title: "Unknown outcome", message: `${e.message} — it may or may not have been created; retrying with the same mobile is safe.`, partial: true };
}
